import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { RiCheckboxCircleLine, RiErrorWarningLine } from "@remixicon/react";
import pluralize from "pluralize";

import BaseShippingDialog from "./BaseShippingDialog";
import { ShippingDialogProps } from "../../../types/Shipping";
import {
  DataplorApiError_T,
  DataplorApiErrorItem_T,
  useDataPlorApi,
} from "../../../hooks/useDataplorApi";

import useGaneshApi from "../../../hooks/useGaneshApi";
import { AuthContextType, useAuth } from "../../../contexts/AuthContext";

type ConfirmManualError_T = DataplorApiErrorItem_T & {
  shipment_id: number;
  delivery_name: string;
};

const MarkDeliveredDialog: React.FC<ShippingDialogProps> = (props) => {
  const userAuth: AuthContextType = useAuth();
  const theme = useTheme();
  const { apiRequest } = useGaneshApi();
  const { postDataplorApi } = useDataPlorApi();

  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<ConfirmManualError_T[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const { selectedData } = props;
  const {
    fullySelectedGroups,
    partiallySelectedGroups,
    totalSelectedShipments,
  } = selectedData;

  const selectedShipments = [
    ...fullySelectedGroups.flatMap((group) => group.shipments),
    ...partiallySelectedGroups.flatMap((group) => group.shipments),
  ];

  const uniqueShipmentIds = Array.from(
    new Set(
      selectedShipments
        .map((shipment) => shipment.fgs_id)
        .filter((id): id is number => typeof id === "number")
    )
  );

  const { mutateAsync: postMarkDelivered, isPending } = useMutation({
    mutationFn: async () => {
      setProgress(0);
      setErrors([]);
      setIsComplete(false);
      for (let i = 0; i < uniqueShipmentIds.length; i++) {
        const shipmentId = uniqueShipmentIds[i];
        const shipment = selectedShipments.find((s) => s.fgs_id === shipmentId);
        try {
          await postDataplorApi(
            `/v3/admin/fulfillment_group_shipments/${shipmentId}/confirm_manual`,
            {}
          );
        } catch (error) {
          const errorData = error as DataplorApiError_T;
          const apiErrors = (errorData.errors ||
            []) as DataplorApiErrorItem_T[];
          setErrors((prev) => [
            ...prev,
            ...apiErrors.map((err) => ({
              ...err,
              shipment_id: shipmentId,
              delivery_name: shipment?.delivery_name || "No Name",
            })),
          ]);
        }
        setProgress(((i + 1) / uniqueShipmentIds.length) * 100);
      }

      return true;
    },
    onSuccess: () => {
      setIsComplete(true);
      const deliveryEvents = selectedShipments.map((s) => ({
        delivery_schedule_id: s.delivery_fulfillment_id,
      }));
      if (deliveryEvents.length > 0) {
        postVerifyDeliveries({
          delivery_events: deliveryEvents,
          admin_id: userAuth.authAttributes?.id ?? null,
        });
      }
    },
    onError: () => {
      setIsComplete(true);
    },
  });

  const { mutate: postVerifyDeliveries, isPending: isVerifyingDeliveries } =
    useMutation({
      mutationFn: async (payload: any) => {
        const response = await apiRequest(`/schedule/batch-verify-deliveries`, {
          method: "POST",
          body: payload,
        });
        return response as any;
      },
      onSuccess: () => {},
      onError: (error) => {
        console.error(error);
      },
    });

  const handleSubmit = () => {
    postMarkDelivered();
  };

  return (
    <BaseShippingDialog
      {...props}
      title="Mark as Delivered"
      onSubmit={handleSubmit}
      submitText="Mark Delivered"
    >
      <Box>
        {selectedShipments.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2">
                Please select at least one shipment to proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {selectedShipments.length > 0 && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to mark {totalSelectedShipments} selected{" "}
              {pluralize("shipment", totalSelectedShipments)} as delivered?
            </Typography>

            {(isPending || isComplete) && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Processed: {Math.round(progress)}% (
                  {Math.floor((progress / 100) * uniqueShipmentIds.length)} of{" "}
                  {uniqueShipmentIds.length})
                </Typography>
              </Box>
            )}

            {isComplete && errors.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }} icon={false}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RiCheckboxCircleLine size={20} color="success.main" />
                  <Typography variant="body2">
                    Successfully marked {uniqueShipmentIds.length}{" "}
                    {pluralize("shipment", uniqueShipmentIds.length)} as
                    delivered.
                  </Typography>
                </Box>
              </Alert>
            )}

            {isComplete && errors.length > 0 && (
              <>
                <Alert severity="error" sx={{ mt: 2 }} icon={false}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RiErrorWarningLine size={20} color="error.main" />
                    <Typography variant="body2">
                      Failed to mark {errors.length}{" "}
                      {pluralize("shipment", errors.length)} as delivered:
                    </Typography>
                  </Box>
                </Alert>
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {errors.map((error) => (
                    <li key={error.shipment_id}>
                      {error.delivery_name} (ID: {error.shipment_id}):{" "}
                      {error.message}
                    </li>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </BaseShippingDialog>
  );
};

export default MarkDeliveredDialog;
